import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import Sparkline from 'components/shared/charts/Sparkline';

/**
 * StatCard - tarjeta de estadística (MUI + tokens OTRIX). Superficie monocroma con acento
 * dorado en el ícono; la tendencia usa color semántico (verde/rojo/gris), no el acento.
 *
 * @param {string} title - Título de la estadística
 * @param {string|number} value - Valor principal
 * @param {string} subtitle - Subtítulo o descripción
 * @param {node|string} icon - Ícono MUI (preferido) o emoji
 * @param {string} trend - up, down, neutral
 * @param {string} trendValue - Valor del trend (ej: "+12%")
 * @param {number[]} sparkline - Serie de tendencia; se dibuja al pie de la tarjeta
 */
const TREND = {
  up: { Icon: TrendingUp, color: 'trend.up' },
  down: { Icon: TrendingDown, color: 'trend.down' },
  neutral: { Icon: TrendingFlat, color: 'trend.flat' }
};

function StatCard({
  title,
  value,
  subtitle = undefined,
  icon = undefined,
  trend = undefined,
  trendValue = undefined,
  sparkline = undefined
}) {
  const tr = trend ? TREND[trend] : null;
  const hasSparkline = Array.isArray(sparkline) && sparkline.length > 1;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'surface.raised',
        border: '1px solid',
        borderColor: 'hairline.main',
        borderRadius: 2,
        boxShadow: (theme) => theme.palette.shadow?.card,
        overflow: 'hidden',
        transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), border-color 0.2s',
        '&:hover': { transform: 'translateY(-2px)', borderColor: 'hairline.strong' }
      }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'gold.soft',
                color: 'gold.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.4rem',
                flex: 'none'
              }}>
              {icon}
            </Box>
          )}
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }} noWrap>
            {title}
          </Typography>
        </Stack>
        {tr && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ px: 1, py: 0.4, borderRadius: 999, bgcolor: 'surface.overlay', flex: 'none' }}>
            <tr.Icon sx={{ fontSize: 15, color: tr.color }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: tr.color }}>
              {trendValue}
            </Typography>
          </Stack>
        )}
      </Box>

      <Box sx={{ px: 2, pb: hasSparkline ? 1 : 2, flex: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {hasSparkline && <Sparkline data={sparkline} />}
    </Box>
  );
}

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.node,
  trend: PropTypes.oneOf(['up', 'down', 'neutral']),
  trendValue: PropTypes.string,
  sparkline: PropTypes.arrayOf(PropTypes.number)
};

export default StatCard;
